import { Divider, Grid, Stack, Typography } from "@material-ui/core";
import { contactCellRenderer } from "./ContactTableDesktopRow";
import { useTranslation } from "react-i18next";
import { ContactTableRooms } from "./ContactTableRooms";
import { ListingBooleanPropertiesDetailsBag } from "components/_dashboard/listingsMatch/ListingPropertiesAndAdditionalDetails";
import { fToNow } from "utils/formatTime";
import { ContactTableTag } from "./ContactTableTags";
import { ContactTableProperty } from "./ContactTableProperty";
import useSettings from "hooks/useSettings";
import useFirestore from "hooks/useFirestore";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ValidPropertyType } from "./ContactTableProperty";

type ContactTableNestedRowDetailsProps = {
  contact: RealEstateContact;
};

export function ContactTableNestedRowDetails({
  contact
}: ContactTableNestedRowDetailsProps): JSX.Element {
  const { t: translate } = useTranslation();

  const { userSettings } = useFirestore();

  const { themeDirection } = useSettings();

  const isRealEstateUser = userSettings?.crmType === "realEstate";

  const isRTL = themeDirection === "rtl";

  return (
    <>
      <Divider />
      <Grid container rowSpacing={2}>
        {contactDetails.map(({ id, index, onlyRealEstate }) => {
          const skipContactDetail = onlyRealEstate && !isRealEstateUser;

          if (skipContactDetail) return null;

          const CellRenderer = contactCellRenderer[id];

          let content: JSX.Element | string = <CellRenderer contact={contact} />;

          const isTimestampField = [
            "createdTime",
            "updatedTime",
            "lastBulkMessageReceivedTime"
          ].includes(id);

          if (isTimestampField) {
            const timestamp = contact[id];

            if (!timestamp) return null;

            content = fToNow(timestamp as string, isRTL);
          }

          const isPropertyArray = ["interestedInLocations", "interestedInPropertyTypes"].includes(
            id
          );

          if (isPropertyArray) {
            content = <ContactTableProperty type={id as ValidPropertyType} contact={contact} />;
          }

          if (id === "tags") {
            content = <ContactTableTag contact={contact} />;
          }

          return (
            <Grid item order={index} xs={6} md={4} key={id}>
              <Stack gap={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  {translate(`contactsPage.tableColumns.${id}`)}
                </Typography>
                <Typography variant="subtitle2" color="primary.main">
                  {content}
                </Typography>
              </Stack>
            </Grid>
          );
        })}
        <ContactTableRooms contact={contact} />
      </Grid>
      <ListingBooleanPropertiesDetailsBag contact filterFalsyValue data={contact} />
    </>
  );
}

type ContactDetailIds = keyof Pick<
  RealEstateContact,
  | "tags"
  | "roomsMax"
  | "roomsMin"
  | "createdTime"
  | "updatedTime"
  | "customerType"
  | "customerBudget"
  | "customerSeriousness"
  | "interestedInLocations"
  | "interestedInPropertyTypes"
  | "lastBulkMessageReceivedTime"
  | "contactOrigin"
  | "contactOriginAdditionalInfo"
>;

type ContactDetail = {
  id: ContactDetailIds;
  index: number;
  onlyRealEstate?: boolean;
};

const contactDetails: ContactDetail[] = [
  { id: "customerType", index: 0, onlyRealEstate: true },
  { id: "customerBudget", index: 0, onlyRealEstate: true },
  { id: "tags", index: 0 },
  { id: "interestedInLocations", index: 0, onlyRealEstate: true },
  { id: "interestedInPropertyTypes", index: 0, onlyRealEstate: true },
  { id: "customerSeriousness", index: 1, onlyRealEstate: true },
  { id: "createdTime", index: 1 },
  { id: "updatedTime", index: 1 },
  { id: "lastBulkMessageReceivedTime", index: 1 },
  { id: "contactOrigin", index: 1 },
  { id: "contactOriginAdditionalInfo", index: 1 }
];

export const timestampContactDetails: ContactDetailIds[] = [
  "createdTime",
  "updatedTime",
  "lastBulkMessageReceivedTime"
];
